/** Shared Quartix fleet-config form fields (controlled).
 *
 * Used by the creation wizard's fleet step and the edit drawer's fleet form, so
 * both surfaces render an identical field set. Sync type is fixed to "Pull"
 * (Quartix is pull-only). On edit, the password arrives masked — leaving it
 * unchanged preserves the stored secret (backend edit-by-overwrite).
 */
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { ChipListField } from './wizard/ChipListField';
import { FLEET_REGIONS, VEHICLE_STATUSES, type FleetWizardData } from './wizard/types';

interface Props {
  value: FleetWizardData;
  onChange: (partial: Partial<FleetWizardData>) => void;
  showEnabled?: boolean;
  disabled?: boolean;
}

export function FleetFields({ value, onChange, showEnabled = true, disabled }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <Grid container spacing={2}>
      {showEnabled && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={value.enabled}
                disabled={disabled}
                onChange={(e) => onChange({ enabled: e.target.checked })}
              />
            }
            label="Enabled"
          />
        </Grid>
      )}

      <Grid item xs={12} sm={4}>
        <TextField
          select
          fullWidth
          size="small"
          label="Region"
          value={value.region}
          disabled={disabled}
          onChange={(e) => onChange({ region: e.target.value as FleetWizardData['region'] })}
        >
          {FLEET_REGIONS.map((r) => (
            <MenuItem key={r} value={r}>
              {r.toUpperCase()}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          label="Sync type"
          value="Pull (cron)"
          disabled
          helperText="Quartix is pull-only"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          required
          fullWidth
          size="small"
          label="Customer ID"
          value={value.customer_id}
          disabled={disabled}
          onChange={(e) => onChange({ customer_id: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          fullWidth
          size="small"
          label="Username"
          value={value.username}
          disabled={disabled}
          onChange={(e) => onChange({ username: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          fullWidth
          size="small"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          value={value.password}
          disabled={disabled}
          onChange={(e) => onChange({ password: e.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((s) => !s)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          label="Application ID"
          value={value.application_id}
          disabled={disabled}
          onChange={(e) => onChange({ application_id: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          label="Site name"
          value={value.site_name}
          disabled={disabled}
          onChange={(e) => onChange({ site_name: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          label="User"
          value={value.user}
          disabled={disabled}
          onChange={(e) => onChange({ user: e.target.value })}
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={value.keep_location_history}
                disabled={disabled}
                onChange={(e) => onChange({ keep_location_history: e.target.checked })}
              />
            }
            label="Keep location history"
          />
          <FormControlLabel
            control={
              <Switch
                checked={value.unassign_if_not_in_api}
                disabled={disabled}
                onChange={(e) => onChange({ unassign_if_not_in_api: e.target.checked })}
              />
            }
            label="Unassign if not in API"
          />
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Hidden vehicle statuses
        </Typography>
        <ChipListField
          label=""
          options={VEHICLE_STATUSES}
          values={value.hidden_vehicle_statuses}
          onChange={(v) => onChange({ hidden_vehicle_statuses: v })}
          disabled={disabled}
          placeholder="Add status…"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Route unassign events
        </Typography>
        <ChipListField
          label=""
          values={value.route_unassign_events}
          onChange={(v) => onChange({ route_unassign_events: v })}
          disabled={disabled}
          placeholder="e.g. Ignition-Off"
        />
      </Grid>
    </Grid>
  );
}
